import React, { useState, useEffect, useContext } from "react";
import { Card, Table, Button, Form, OverlayTrigger, Tooltip, Spinner} from "react-bootstrap";
import NotyfContext from "../contexts/NotyfContext";
import { X, Check, Edit2, Trash2, XCircle, MessageSquare, File} from "react-feather";

import { useTable, useSortBy, useFilters} from "react-table";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faSort,
  faSortUp,
  faSortDown,
  faFilePdf,
} from "@fortawesome/free-solid-svg-icons";

import authenticatedFetch from "../utils/fetch"
import { caseInsensitiveSort, getReminderSentDisplay } from "./reactTableUtils";
import EditableExpires from "./EditableExpires";
import { UserSettingsContext } from "../contexts/UserSettingsContext";

import rawImmunizationTypes from "./immunizationTypes";
import LinkedDocumentsDisplay from "./LinkedDocumentsDisplay";
import LinkedDocumentSelect from "./LinkedDocumentSelect";
import { getDocumentDisplayLabel } from "./linkedDocumentUtils";

const Immunization = ({ id, providerId, immunizationType, immunizationDate, declined, notes, reminderSent, documents, refreshList, canEdit, onCancelEdit, startInEditMode=false }) => {
  const [editing, setEditing] = useState(startInEditMode);
  const [localImmunizationType, setLocalImmunizationType] = useState(immunizationType);
  const [localImmunizationDate, setLocalImmunizationDate] = useState(immunizationDate);
  const [localDeclined, setLocalDeclined] = useState(declined);
  const [localNotes, setLocalNotes] = useState(notes);
  const [selectedDocuments, setSelectedDocuments] = useState([]);
  const [documentFilenames, setDocumentFilenames] = useState([]);

  const { isH3Admin, authorities, documentsRefresh, setDocumentsRefresh } = useContext(UserSettingsContext);
  const notyf = useContext(NotyfContext);

  const setInitialDocumentFilenames = () => {
    let initialDocumentFilenames = []
    let initialSelectedDocuments = []
    if (documents) // undefined if we're excluding the column due to permissions
    {
      for (var i=0; i<documents.length; i++)
      {
        initialDocumentFilenames.push(documents[i].filename);
        initialSelectedDocuments.push({value: documents[i].filename, label: getDocumentDisplayLabel(documents[i])});
      }
    }

    setDocumentFilenames(initialDocumentFilenames);
    setSelectedDocuments(initialSelectedDocuments);
  }

  useEffect(() => {
    setInitialDocumentFilenames();
  }, [id, providerId, documents]);
  
  useEffect(() => {
    if (!editing) {
      setLocalImmunizationDate(immunizationDate);
      setLocalNotes(notes);
      setInitialDocumentFilenames();
    }
  }, [immunizationDate, notes, documents]);

  const handleDocumentChanged = (documents, e) => {
    setSelectedDocuments(documents);

    let newDocumentFilenames = []
    for (var i=0; i<documents.length; i++)
    {
      newDocumentFilenames.push(documents[i].value);
    }

    setDocumentFilenames(newDocumentFilenames);
  }

  function handleDeleteClicked(e) {
    e.preventDefault();
    if (window.confirm("Are you sure you want to delete this item?"))
    {
      authenticatedFetch({
        method: "DELETE",
        path: "immunization/" + id,
        successHandler: function(result) {
          refreshList();
          setDocumentsRefresh(documentsRefresh + 1);
          notyf.open({type: "success", message: "Deleted!", duration: 5000, dismissable: true, ripple: true, background: '#3AAFA9'});
        },
        errorHandler: function(error) {
          notyf.open({type: "error", message: "Something went wrong; please try again", duration: 5000, dismissable: true, ripple: true});
        }
      });
    }
  }

  function handleEditClicked(e) {
    e.preventDefault();
    setEditing(true);
  }

  function cancelEdit(e) {
    e.preventDefault();
    setEditing(false);
    setLocalImmunizationType(immunizationType);
    setLocalImmunizationDate(immunizationDate);
    setLocalDeclined(declined);
    setLocalNotes(notes);
    setInitialDocumentFilenames();

    if (onCancelEdit)
    {
      onCancelEdit();
    }
  }

  function saveEdit(e) {
    e.preventDefault();
    if (localImmunizationType === '' && localImmunizationDate === null)
    {
      alert("Please enter a immunization type or date");
      return;
    }

    setEditing(false);

    authenticatedFetch({
      method: id ? "PUT" : "POST",
      requestData: {providerId: parseInt(providerId), immunizationType: localImmunizationType, immunizationDate: localImmunizationDate, declined: localDeclined, notes: localNotes, documentFilenames: documentFilenames},
      path: id ? "immunization/" + id  : "immunization",
      successHandler: function(result) {
        refreshList();
        setDocumentsRefresh(documentsRefresh + 1);
        notyf.open({type: "success", message: id ? "Updated!" : "Saved!", duration: 5000, dismissable: true, ripple: true, background: '#3AAFA9'});
      },
      errorHandler: function() {
        setEditing(true);
        setLocalImmunizationType(immunizationType);
        setLocalImmunizationDate(immunizationDate);
        setLocalDeclined(declined);
        setLocalNotes(notes);
        setInitialDocumentFilenames();
        notyf.open({type: "error", message: "Something went wrong; please try again", duration: 5000, dismissable: true, ripple: true});
      }
    });    
  }

  function handleImmunizationTypeChanged(e) {
    e.preventDefault();
    setLocalImmunizationType(e.target.value);
  }

  function handleImmunizationDateChanged(val, e) {
    e.preventDefault();
    setLocalImmunizationDate(val);
  }

  function handleDeclinedChanged(e) {
    setLocalDeclined(e.target.checked);
  }

  function handleNotesChanged(e) {
    e.preventDefault();
    setLocalNotes(e.target.value);
  }

  var notesReadOnly = '';
  if (localNotes && localNotes.length > 0) {
    notesReadOnly = localNotes.split("\n").map(function(item, idx) {
      return ( 
          <span key={idx}>
              {item}
              <br />
          </span>
       )
    });
  }

  let immunizationTypeList = rawImmunizationTypes.map((immunizationType, i) => {
    return (
      <option key={i} value={immunizationType.value}>{immunizationType.label}</option>
    )
  }, this);

  var selectedImmunizationTypeLabel = '';
  if (localImmunizationType !== '')
  {
      for (let i = 0; i < rawImmunizationTypes.length; i++)
      {
          if (localImmunizationType === rawImmunizationTypes[i].value)
          {
            selectedImmunizationTypeLabel = rawImmunizationTypes[i].label;
            break;
          }
      }
  }

  return (
    <>
    {editing ?
    <td>
      <Form.Select size="sm" value={localImmunizationType} onChange={(e) => {handleImmunizationTypeChanged(e)}} style={{width: "auto"}} >
          {immunizationTypeList}
      </Form.Select>
    </td>
      :
    <td style={
      !localImmunizationType || localImmunizationType.length === 0
        ? {
            backgroundColor: "rgba(220,53,69,0.3)",
            // cancel the striped bg for this cell
            ["--bs-table-accent-bg"]: "transparent"
          }
        : {}
    }>
        {selectedImmunizationTypeLabel}
      </td>
    }
    <td>
    <EditableExpires warnWithColors={false} localExpires={localImmunizationDate} localActive={true} editingExpires={editing} handleExpiresChanged={handleImmunizationDateChanged} />
    </td>
    {editing?
    <td> 
      <Form.Check type="checkbox" checked={localDeclined} onChange={(e) => {handleDeclinedChanged(e)}} />
    </td>
    :
    <td>
      {localDeclined ? <XCircle className="text-danger" /> : <>&nbsp;</>}
    </td>
    }
    {editing ?
    <td>
      <Form.Control style={{height: "120px"}} size="sm" as="textarea" maxLength={511} value={localNotes} onChange={(e) => {handleNotesChanged(e)}} />
    </td>
    :
    <td>
      {notesReadOnly}
    </td>
    }
    {(isH3Admin || (authorities.includes("PROVIDER_DOCUMENT_VIEW") || authorities.includes("PROVIDER_DOCUMENT_EDIT")))&&
    <>
      {editing && (isH3Admin || authorities.includes("PROVIDER_DOCUMENT_EDIT"))?
      <td>
        <LinkedDocumentSelect entityType={"immunization"} providerId={providerId} handleDocumentChanged={handleDocumentChanged} selectedDocuments={selectedDocuments}/>
      </td>
        : 
      <td>
        <LinkedDocumentsDisplay entityType={"immunization"} documents={documents} entityId={id}/>
      </td> 
      }
    </>
    }
    <td>{getReminderSentDisplay(reminderSent)}</td>
    <td style={{width: '70px'}}>
      { canEdit?
        <>
        {editing ? 
        <>
          <X style={{'height': '16px', 'width': '16px'}} cursor="pointer" onClick={(e) => {cancelEdit(e)}} />
          &nbsp;<Check style={{'height': '16px', 'width': '16px'}} cursor="pointer" onClick={(e) => {saveEdit(e)}} className="text-success"/>
        </>
        :
        <>
          <Edit2 style={{'height': '12px', 'width': '12px'}} cursor="pointer" onClick={(e) => {handleEditClicked(e)}}/>&nbsp;&nbsp;
          <Trash2 style={{'height': '12px', 'width': '12px'}} cursor="pointer" onClick={(e) => {handleDeleteClicked(e)}}/>
        </>
        }
        </>
        : <>&nbsp;</>
      }
    </td>
  </>
  )
}


const ImmunizationsTable = ({providerId, addingNew, onCancelAddNew, list, isLoaded, error, canEdit, refreshList}) => {
  const data = list;

  const { isH3Admin, authorities } = useContext(UserSettingsContext);

  const columns = React.useMemo(() => {
    const baseColumns = [  
      {
        accessor: "id",
        key: "id",
      },
      {
        Header: "Type",
        accessor: "immunizationType",
        key: "immunizationType",
      },
      {
        Header: "Date",
        accessor: "immunizationDate",
        key: "immunizationDate",
      },
      {
        Header: "Declined",
        accessor: "declined",
        key: "declined",
      },
      {
        Header: "Details",
        accessor: "notes",
        key: "notes",
      }
    ];

    if(isH3Admin || (authorities.includes("PROVIDER_DOCUMENT_VIEW") || authorities.includes("PROVIDER_DOCUMENT_EDIT")))
    {
      baseColumns.push(
      {
        Header: () => (
          <OverlayTrigger
            placement="top"
            overlay={<Tooltip>Documents</Tooltip>}
          >
            <span>
              <File size={16} />
            </span>
          </OverlayTrigger>
        ),
        accessor: "documents",
        key: "documents",
      });
    }
    
    baseColumns.push(
      {
        Header: () => (
          <OverlayTrigger
            placement="top"
            overlay={<Tooltip>Reminder Sent</Tooltip>}
          >
            <span>
              <MessageSquare size={16} />
            </span>
          </OverlayTrigger>
        ),
        accessor: "reminderSent",
        key: "reminderSent",
      },
      {
        Header: "",
        accessor: "icons",
        key: "icons",
      }
    );

    return baseColumns;
  }, [isH3Admin, authorities]);

  const { getTableProps, getTableBodyProps, headerGroups, rows, prepareRow } =
  useTable(
    {
      columns,
      data,
      autoResetSortBy: false,
      autoResetFilters: false,
      initialState: {
        hiddenColumns: ['id'],
        sortBy: [
          {
            id: 'immunizationDate',
            desc: true
          },  
          {
              id: 'immunizationType',
              desc: false
          } 
        ],
      },
      sortTypes: {
        alphanumeric: (row1, row2, columnName) => caseInsensitiveSort({row1, row2, columnName})
      }
    },
    useFilters,
    useSortBy,
  );

  return (
    <Table className="table-sm" striped bordered hover {...getTableProps()}>
    <thead>
      {headerGroups.map((headerGroup) => (
        <tr {...headerGroup.getHeaderGroupProps()}>
          {headerGroup.headers.map((column) => {
            const { key, ...restHeaderProps } = (column.id === "icons" || column.id === "notes" || column.id === "documents" || column.id === "reminderSent") ? column.getHeaderProps() : column.getHeaderProps(column.getSortByToggleProps());
            return (
              <th {...restHeaderProps} key={key}>
                {column.render("Header")}
                {(column.id !== "icons" && column.id !== "notes" && column.id !== "documents" && column.id !== "reminderSent") ? (
                  <>
                  {column.isSorted ? (
                      column.isSortedDesc ? (
                        <FontAwesomeIcon icon={faSortDown} className="ms-2" />
                      ) : (
                        <FontAwesomeIcon icon={faSortUp} className="ms-2" />
                      )
                    ) : (
                      <FontAwesomeIcon icon={faSort} className="ms-2" />
                  )}
                  </>
                ) : <></>
                }
              </th>
            )
          }
          )}
        </tr>
      ))}
    </thead>
    <tbody {...getTableBodyProps()}>
      {
      error ? (<tr><td colSpan={5}>Something went wrong; please reload the page...</td></tr>) : ( 
        !isLoaded ? (<tr><td colSpan={5}>Loading...</td></tr>) : (
          rows.map((row) => {
          prepareRow(row);
          const { key, ...restRowProps } = row.getRowProps();
          return (
            <tr {...restRowProps} key={key}>
              <Immunization providerId={providerId} id={row.values.id} key={row.values.id} immunizationType={row.values.immunizationType ? row.values.immunizationType : ""} immunizationDate={row.values.immunizationDate} declined={row.values.declined == null ? false : row.values.declined} notes={row.values.notes ? row.values.notes : ""} reminderSent={row.values.reminderSent} documents={row.values.documents} refreshList={refreshList} canEdit={canEdit}/>
            </tr>
          );
        })))
      }
      {
      addingNew ?
        <tr><Immunization providerId={providerId} key={0} startInEditMode={true} certification={""} immunizationType={""} immunizationDate={null} declined={false} notes={""} reminderSent={""} documents={[]} onCancelEdit={onCancelAddNew} canEdit={canEdit} refreshList={refreshList}/></tr>
      : <></>
      }
    </tbody>
  </Table>
  )
}

const Immunizations = ({providerId, canEdit}) => {
  const [error, setError] = useState(null);
  const [isLoaded, setIsLoaded] = useState(false);
  const [data, setData] = useState([]);
  const [addingNew, setAddingNew] = useState(false);
  const [isExporting, setIsExporting] = useState(false);

  const { documentsRefresh, tasksRefresh } = useContext(UserSettingsContext);

  const notyf = useContext(NotyfContext);

  useEffect(() => {
    refreshList();
  }, [providerId, documentsRefresh, tasksRefresh])

  function refreshList()
  {
    setAddingNew(false);
    authenticatedFetch({
      path: "immunization/provider/" + providerId,
      successHandler: function(result) {
        setIsLoaded(true);
        setData(result);
      },
      errorHandler: function() {
        setIsLoaded(true);
        setError(true);
      }
    });
  }

  const handleAddNewClicked = (e) => {
    e.preventDefault();
  
    setAddingNew(true);
  }

  const onCancelAddNew = () => {
    setAddingNew(false);
  }

  const handleExportClicked = (e) => {
    if (isExporting) {
      return;
    }

    const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;

    e.stopPropagation();
    setIsExporting(true);
    authenticatedFetch({
      method: "GET",
      path:  "report/immunization/provider/" + providerId,
      additionalHeaders: {
        "X-Timezone": timezone,
      },
      successHandler: function(result) {
        window.open(result.url);
        setIsExporting(false);
      },
      errorHandler: function(error) {
        setIsExporting(false);
        notyf.open({type: "error", message: "Something went wrong; please try again", duration: 5000, dismissable: true, ripple: true});
      }
    });
  }

  var list = [];
  if (isLoaded)
  {
    list = data.map((item, i) => {
      return {key: item.id, id: item.id, immunizationType: item.immunizationType, immunizationDate: item.immunizationDate, declined: item.declined, notes: item.notes, reminderSent: item.reminderSent, documents: item.documents, updated: item.updated, updatedBy: item.updatedBy}
    }, this);
  }

  return (
    <Card>
      <Card.Header style={{marginBottom: '-20px'}}>
        <Card.Title>Immunizations
          { isExporting ? 
            <Spinner animation="border" size="sm" variant="primary" className="float-end" style={{marginRight: '12px', marginTop: '7px', marginBottom: '7px'}}/>
            :
            <Form.Label style={{display: "inline", fontSize: "0.8em", fontWeight: "normal", marginTop: "7px", marginRight: "3px"}} className="float-end">
              Export:&nbsp;&nbsp;
              <FontAwesomeIcon onClick={(e) => {handleExportClicked(e)}} icon={faFilePdf} size="lg" style={{color: '#2B7A78', cursor: "pointer"}}/>
              &nbsp;
            </Form.Label>
          }
        </Card.Title>
      </Card.Header>
      <Card.Body>
        <ImmunizationsTable list={list} addingNew={addingNew} onCancelAddNew={onCancelAddNew} providerId={providerId} error={error} isLoaded={isLoaded} canEdit={canEdit} refreshList={refreshList}/>
        {canEdit?
          <span className="float-end" style={{marginTop: '6px'}}>
            <Button size="sm" variant="primary" className="float-end" onClick={(e) => {handleAddNewClicked(e)}}>+ Add New Immunization</Button>
          </span>
          : <></>
        }
      </Card.Body>
    </Card>
  )
}

export default Immunizations;
